import { useMutateRelationship__ALPHA } from '@cube3/state/src/redux/components/Hooks/useMutateRelationship';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { mutations } from '@cube3/state/src/redux/ducks/resource-edges';
import { compose } from '@cube3/state/src/utils/component-helpers';
import Button from '@cube3/ui/src/Buttons/Button';
import { ModalNoticeUI } from '@cube3/ui/src/Modal/ModalNoticeUI';
import { useModalActions } from '../../../../../../app/layout/Modals/modalActions';
import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { getFormValues, InjectedFormProps, reduxForm } from 'redux-form';
import { WorkspaceTeamMembersPicker } from '../prefabs/WorkspaceTeamMembersPicker';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { ResourceIdentifier } from '@cube3/common/model/resource-types';

interface ModalReceivedProps {
  modalContext: {
    team: string;
  };
}

type Props = ModalReceivedProps &
  InjectedFormProps & {
    formValues: {
      accounts: ResourceIdentifier<'account'>[];
      teamName: string;
    };
  };

const mapStateToProps = (state, ownProps) => {
  const values = getFormValues('add_team_member')(state);
  return {
    form: ownProps.form,
    formValues: values
  };
};

const AddTeamMemberModal = (props: Props) => {
  const { modalContext, form, formValues, valid } = props;
  const { team } = modalContext;
  const [workspaceId] = useCurrentWorkspace();
  const { previousModal } = useModalActions();

  const { resources: teamAccounts, loading: loadingTeamMembers } =
    useResourceList__ALPHA({
      resourceType: 'team',
      resourceId: team,
      edgeType: 'account',
      edgeLabel: 'members'
    });

  const excludedAccounts = useMemo(() => {
    if (!teamAccounts) return null;
    return teamAccounts.map((acc) => acc.id);
  }, [teamAccounts]);

  const [addMembers, addMembersStatus] = useMutateRelationship__ALPHA({
    ancestor: { type: 'team', id: team },
    relationship: 'members',
    cacheInvalidator: useCallback(
      (res, mut, anc, rel) => [
        {
          type: 'workspace',
          id: workspaceId,
          relationship: 'accounts'
        },
        { type: anc.type, id: anc.id }
      ],
      [workspaceId]
    ),
    options: { optimize: true }
  });
  const accounts = formValues?.accounts;
  const onClickAdd = useCallback(() => {
    if (!accounts?.length) return;
    addMembers(
      accounts.map((m) => ({
        resource: m,
        mutationType: mutations.MUTATION_ADD
      }))
    );
  }, [addMembers, accounts]);

  const loading =
    addMembersStatus &&
    addMembersStatus !== statuses.SUCCESS &&
    addMembersStatus !== statuses.FAILED;

  useEffect(() => {
    if (addMembersStatus === statuses.SUCCESS) {
      previousModal();
    }
  }, [addMembersStatus, previousModal]);

  return (
    <ModalNoticeUI
      title="Add Member"
      loading={loading || loadingTeamMembers}
      component="form"
      onCloseEvent={previousModal}
      footerRightComponent={
        <>
          <Button text="Cancel" colorVariant="ghost2" onClick={previousModal} />
          <Button
            text="Add"
            colorVariant="filled1"
            onClick={onClickAdd}
            disabled={!valid || !accounts || !accounts.length}
          />
        </>
      }
    >
      <div style={{ minHeight: 400 }}>
        <WorkspaceTeamMembersPicker
          label="Members"
          inputFieldPlaceholder="Enter a user name"
          form={form}
          autoFocus={true}
          excludedAccounts={excludedAccounts}
        />
      </div>
    </ModalNoticeUI>
  );
};

export default compose(AddTeamMemberModal)(
  connect(mapStateToProps),
  reduxForm({
    form: 'add_team_member',
    destroyOnUnmount: true
  })
);
